import { throttle } from 'lodash';
import { NetworkStatus } from '@apollo/client';
import { handleFetchMore } from '@src/util/handleFetchMore';
import {
  usePublishedOpeningCandidatesQuery,
  RecommendedUsersSortOptionEnum,
  PublishedOpeningCandidatesQuery,
  SignalEntityTypeEnum,
  SignalTypeEnum,
} from '@src/graphql/generated';
import { recordSignals } from '@src/graphql/signals/actions';
import {
  UsePublishedOpeningRecommendedUsersParams,
  UsePublishedOpeningRecommendedUsersValue,
} from './interfaces/usePublishedOpeningRecommendedUsers.interface';

export const FIRST_BATCH_LIMIT = 12;
export const NEXT_BATCH_LIMIT = 3;

export const usePublishedOpeningRecommendedUsers = ({
  recruiterCurrencyCode,
  openingId,
}: UsePublishedOpeningRecommendedUsersParams): UsePublishedOpeningRecommendedUsersValue => {
  const { data, networkStatus, fetchMore } = usePublishedOpeningCandidatesQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      openingId: openingId,
      paginationOptions: {
        limit: FIRST_BATCH_LIMIT,
        sortBy: RecommendedUsersSortOptionEnum.RELEVANCE,
      },
      recruiterCurrencyCode: recruiterCurrencyCode,
    },
  });

  const pagination = data?.opening.recommendedUsers.pagination;
  const feed = data?.opening.recommendedUsers.result || [];

  const isFetchMoreLoading = networkStatus === NetworkStatus.fetchMore;
  const isInitialLoading = networkStatus <= 2;
  const isLoading = isInitialLoading || isFetchMoreLoading;

  const loadMore = throttle(() => {
    if (!isLoading && pagination?.hasMoreResults) {
      handleFetchMore(fetchMore, {
        updateQuery: (
          previousResult: PublishedOpeningCandidatesQuery,
          { fetchMoreResult }: { fetchMoreResult: PublishedOpeningCandidatesQuery },
        ) => {
          if (!fetchMoreResult) {
            return previousResult;
          }

          const prevResult = previousResult.opening;
          const previousApplicants = previousResult.opening?.recommendedUsers;

          const newResult = fetchMoreResult.opening.recommendedUsers.result;
          const pagination = fetchMoreResult.opening.recommendedUsers.pagination;

          recordSignals({
            currentLimit: previousApplicants.pagination.limit || 0,
            items: previousApplicants.result.map(item => ({
              id: item.userVerification.id,
              type: SignalEntityTypeEnum.USER_VERIFICATION,
            })),
            signalType: SignalTypeEnum.VIEW_USER_VERIFICATION_LIST_ITEM,
          });

          return {
            opening: {
              ...prevResult,
              recommendedUsers: {
                pagination: {
                  ...prevResult.recommendedUsers.pagination,
                  hasMoreResults: pagination.hasMoreResults,
                  limit: pagination.limit,
                  maxCursor: pagination.maxCursor,
                },
                result: [...prevResult.recommendedUsers.result, ...newResult],
              },
            },
          };
        },
        variables: {
          openingId: openingId,
          paginationOptions: {
            limit: NEXT_BATCH_LIMIT,
            minCursor: pagination.maxCursor,
            sortBy: RecommendedUsersSortOptionEnum.RELEVANCE,
          },
          recruiterCurrencyCode: recruiterCurrencyCode,
        },
      });
    }
  }, 200);

  return {
    feed: feed,
    hasMoreResults: !!pagination?.hasMoreResults,
    isFetchMoreLoading: isFetchMoreLoading,
    isInitialLoading: isInitialLoading,
    isLoading: isLoading,
    loadMore: loadMore,
  };
};
